import { useEffect, useState } from "react";
import { t } from "i18next";
import { Input } from "@material-tailwind/react";
import Async from "react-select/async";
import TechnicianSelect from "../../../components/data/technician/TechnicianSelect";
import CustomerSelect from "../../../components/data/customer/CustomerSelect";
import RangeDatePicker from "../../../components/data/date/RangeDatePicker";
import WorkorderStatusSelect from "../../../components/data/workorder-status/WorkorderStatusSelect";
import WorkorderTypeSelect from "../../../components/data/workorder-type/WorkorderTypeSelect";
import WorkorderOriginSelect from "../../../components/data/workorder-origin/WorkorderOriginSelect";
import WorkorderSafetyDiagSelect from "../../../components/data/workorder-safety-diag/WorkorderSafetyDiagSelect";
import { createDateQuery, createDateRangeQuery, createListQuery, createSingleQuery, createTextSearchQuery, SearchQuery } from "../../../services/api/search";
import Switch from "../../../components/switch/Switch";

interface WorkordersFilterFormProps {
  data?: Partial<EasyTech.WorkordersFilterType>;
  onChange: (name: string, value: any) => void;
  onSubmit: () => void;
  submitted?: boolean;
}

export const getQueriesFromFilter = (filters?: Partial<EasyTech.WorkordersFilterType>): SearchQuery[] => {
  const queries: SearchQuery[] = [];
  if (!filters) return queries;

  if (filters.ref) {
    queries.push(createTextSearchQuery(["ref"], filters.ref));
  }

  /*
   * Le flexible porte le code QR, pas l'intervention : on interroge donc le champ à travers la
   * relation. C'est ce que le planificateur cherche — retrouver l'intervention à partir du code
   * collé sur le flexible, et de là les pièces posées.
   *
   * Valeur détourée, comme l'identifiant Odoo : un code arrive rarement tapé à la main, et un
   * espace ou un retour ligne collé avec lui suffirait à ne rien trouver.
   */
  const qrCode = filters.qrCode?.trim();
  if (qrCode) {
    queries.push(createTextSearchQuery(["hoses.qrCode"], qrCode));
  }

  if (filters.technician) {
    if (Array.isArray(filters.technician)) {
      const techIds = filters.technician.map((t: any) => typeof t === "string" ? t : t.id);
      if (techIds.length === 1) {
        queries.push(createSingleQuery("technician.id", techIds[0]));
      } else if (techIds.length > 1) {
        queries.push(createListQuery("technician.id", techIds));
      }
    } else if (typeof filters.technician === "object" && (filters.technician as any).id) {
      queries.push(createSingleQuery("technician.id", (filters.technician as any).id));
    } else if (typeof filters.technician === "string") {
      queries.push(createSingleQuery("technician.id", filters.technician));
    }
  }

  if (filters.customer) {
    if (Array.isArray(filters.customer)) {
      const customerIds = filters.customer.map((c: any) => typeof c === "string" ? c : c.id);
      if (customerIds.length === 1) {
        queries.push(createSingleQuery("customer.id", customerIds[0]));
      } else if (customerIds.length > 1) {
        queries.push(createListQuery("customer.id", customerIds));
      }
    } else if (typeof filters.customer === "object" && (filters.customer as any).id) {
      queries.push(createSingleQuery("customer.id", (filters.customer as any).id));
    } else if (typeof filters.customer === "string") {
      queries.push(createSingleQuery("customer.id", filters.customer));
    }
  }

  if (filters.status && filters.status.length > 0) {
    const statusList = Array.isArray(filters.status) ? filters.status : [filters.status];
    queries.push(createListQuery("status", statusList));
  }

  if (filters.type) {
    if (Array.isArray(filters.type)) {
      const typeIds = filters.type.map((t: any) => typeof t === "string" ? t : t.id);
      if (typeIds.length === 1) {
        queries.push(createSingleQuery("type.id", typeIds[0]));
      } else if (typeIds.length > 1) {
        queries.push(createListQuery("type.id", typeIds));
      }
    } else if (typeof filters.type === "object" && (filters.type as any).id) {
      queries.push(createSingleQuery("type.id", (filters.type as any).id));
    } else if (typeof filters.type === "string") {
      queries.push(createSingleQuery("type.id", filters.type));
    }
  }

  if (filters.origin) {
    if (Array.isArray(filters.origin)) {
      const originIds = filters.origin.map((o: any) => typeof o === "string" ? o : o.id);
      if (originIds.length === 1) {
        queries.push(createSingleQuery("origin.id", originIds[0]));
      } else if (originIds.length > 1) {
        queries.push(createListQuery("origin.id", originIds));
      }
    } else if (typeof filters.origin === "object" && (filters.origin as any).id) {
      queries.push(createSingleQuery("origin.id", (filters.origin as any).id));
    } else if (typeof filters.origin === "string") {
      queries.push(createSingleQuery("origin.id", filters.origin));
    }
  }

  if (filters.safetyDiagStatus && filters.safetyDiagStatus.length > 0) {
    const safetyDiagList = Array.isArray(filters.safetyDiagStatus) ? filters.safetyDiagStatus : [filters.safetyDiagStatus];
    queries.push(createListQuery("safetyDiagStatus", safetyDiagList));
  }

  if (filters.dateRange) {
    const field = filters.dateRange.field || "created";
    if (filters.dateRange.start && filters.dateRange.end) {
      queries.push(createDateRangeQuery(
        field,
        filters.dateRange.start,
        filters.dateRange.end
      ));
    } else if (filters.dateRange.start) {
      queries.push(createDateQuery(
        field,
        filters.dateRange.start,
        "GREATER_THAN"
      ));
    } else if (filters.dateRange.end) {
      queries.push(createDateQuery(
        field,
        filters.dateRange.end,
        "LESS_THAN"
      ));
    }
  }

  if (filters.disabled) {
    queries.push(createSingleQuery("enabled", false));
  }

  if (filters.missingPurchaseOrder) {
    queries.push(createSingleQuery("customer.purchaseOrderMandatory", true));
    queries.push(createSingleQuery("purchaseOrderNumber", null, "IS_NULL"));
  }
  return queries;
};

const WorkordersFilterForm = (props: WorkordersFilterFormProps) => {
  const [formData, setFormData] = useState<Partial<EasyTech.WorkordersFilterType>>({
    status: [],
    ref: "",
    safetyDiagStatus: []
  });

  const dateTypeOptions = [
    { value: "created", label: t("workorder:date.created") },
    { value: "schedule.start", label: t("workorder:date.scheduled_start") },
    { value: "schedule.end", label: t("workorder:date.scheduled_end") },
    { value: "time.start", label: t("workorder:date.real_start") },
    { value: "time.end", label: t("workorder:date.real_end") },
  ];

  useEffect(() => {
    if (props.data) {
      setFormData(props.data);
    } else {
      setFormData({
        status: [],
        ref: "",
        safetyDiagStatus: []
      });
    }
  }, [props.data]);

  // Gestion des changements pour les champs simples
  const handleChange = (value: any, name: string) => {
    const tmp: Partial<EasyTech.WorkordersFilterType> = structuredClone(formData);
    // Gestion spéciale pour le type "status", "technician" et "customer"
    if (name === "status") {
      tmp.status = Array.isArray(value) ? value : [value];
    } else if (name === "safetyDiagStatus") {
      tmp.safetyDiagStatus = Array.isArray(value) ? value : (value ? [value] : []);
    } else if (name === "technician") {
      tmp.technician = value;
    } else if (name === "customer") {
      tmp.customer = value;
    } else {
      tmp[name as keyof Partial<EasyTech.WorkordersFilterType>] = value;
    }
    props.onChange(name, tmp[name as keyof Partial<EasyTech.WorkordersFilterType>]);
    setFormData(tmp);
  };

  // Gestion spécifique des dates d'échéancier
  const handleDateTypeChange = (field: string) => {
    const tmp: Partial<EasyTech.WorkordersFilterType> = structuredClone(formData);
    if (!tmp.dateRange) {
      tmp.dateRange = {
        start: undefined,
        end: undefined,
        field: field
      };
    } else {
      tmp.dateRange = {
        ...tmp.dateRange,
        field: field
      };
    }
    props.onChange("dateRange", tmp.dateRange);
    setFormData(tmp);
  };

  const handleDateRangeChange = (update: [Date | null, Date | null]) => {
    const [start, end] = update;
    const tmp: Partial<EasyTech.WorkordersFilterType> = structuredClone(formData);
    if (!tmp.dateRange) {
      tmp.dateRange = {
        start: start ? start.toISOString() : undefined,
        end: end ? end.toISOString() : undefined,
        field: "created"
      };
    } else {
      tmp.dateRange = {
        ...tmp.dateRange,
        start: start ? start.toISOString() : undefined,
        end: end ? end.toISOString() : undefined
      };
    }
    props.onChange("dateRange", tmp.dateRange);
    setFormData(tmp);
  };

  const submit = (e: any) => {
    e.preventDefault();
    if (formData.dateRange?.start && !formData.dateRange?.end) return;
    if (formData.dateRange?.end && !formData.dateRange?.start) return;
    props.onSubmit();
  };

  return (
    <form onSubmit={submit} className="grid gap-4 p-2" key={"edit-workorders-filter"}>
      <div className="grid grid-cols-1 gap-4">
        <div className="field">
          <label className="block text-sm font-medium text-gray-700 mb-1">{t("workorder:filters:ref:label")}</label>
          <Input
            value={formData.ref || ""}
            onChange={(e) => handleChange(e.target.value, "ref")}
            className="w-full"
            size="md"
            placeholder={t("workorder:filters:ref:placeholder")}
          />
        </div>

        <div className="field">
          <label className="block text-sm font-medium text-gray-700 mb-1">{t("workorder:filters:qrCode:label")}</label>
          <Input
            value={formData.qrCode || ""}
            onChange={(e) => handleChange(e.target.value, "qrCode")}
            className="w-full"
            size="md"
            placeholder={t("workorder:filters:qrCode:placeholder")}
          />
        </div>

        {/* TODO: check values errors */}

        <div className="field">
          <label className="block text-sm font-medium text-gray-700 mb-1">{t("workorder:filters:technician:label")}</label>
          <TechnicianSelect
            onChange={(technicians) => handleChange(technicians, "technician")}
            value={formData.technician || []}
            clearable={true}
            isMulti={true}
          />
        </div>

        <div className="field">
          <label className="block text-sm font-medium text-gray-700 mb-1">{t("workorder:filters:customer:label")}</label>
          <CustomerSelect
            onChange={(customers) => handleChange(customers, "customer")}
            value={formData.customer}
            isClearable={true}
            isMulti={true}
          />
        </div>

        <div className="field">
          <label className="block text-sm font-medium text-gray-700 mb-1">{t("workorder:filters:status:label")}</label>
          <WorkorderStatusSelect
            value={Array.isArray(formData.status) ? formData.status : (formData.status ? [formData.status] : [])}
            onChange={(values) => handleChange(values, "status")}
          />
        </div>

        <div className="field">
          <label className="block text-sm font-medium text-gray-700 mb-1">{t("workorder:filters:type:label")}</label>
          <WorkorderTypeSelect
            value={formData.type}
            onChange={(types) => handleChange(types, "type")}
          />
        </div>

        <div className="field">
          <label className="block text-sm font-medium text-gray-700 mb-1">{t("workorder:filters:origin:label")}</label>
          <WorkorderOriginSelect
            value={formData.origin}
            onChange={(origin) => handleChange(origin, "origin")}
          />
        </div>

        <div className="field">
          <label className="block text-sm font-medium text-gray-700 mb-1">{t("workorder:filters:safetyDiag:label")}</label>
          <WorkorderSafetyDiagSelect
            value={Array.isArray(formData.safetyDiagStatus) ? formData.safetyDiagStatus : (formData.safetyDiagStatus ? [formData.safetyDiagStatus] : [])}
            onChange={(values) => handleChange(values, "safetyDiagStatus")}
          />
        </div>

        <div className="field">
          <label className="block text-sm font-medium text-gray-700 mb-1">{t("workorder:filters:date:label")}</label>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr] gap-4 w-full">
            <div className="flex-1">
              <label className="text-xs text-gray-500 mb-1 block lowercase">{t("common:type", "type")}</label>
              <Async
                cacheOptions
                defaultOptions={dateTypeOptions}
                loadOptions={(inputValue) => {
                  return Promise.resolve(
                    dateTypeOptions.filter((i) =>
                      i.label.toLowerCase().includes(inputValue.toLowerCase())
                    )
                  );
                }}
                value={dateTypeOptions.find(
                  (o) => o.value === (formData.dateRange?.field || "created")
                )}
                theme={(theme) => ({
                  ...theme,
                  colors: {
                    ...theme.colors,
                    primary: "rgb(14 165 233)",
                  },
                })}
                onChange={(e: any) => {
                  if (e) handleDateTypeChange(e.value);
                }}
                className="w-full"
                classNamePrefix="react-select"
                isClearable={false}
              />
            </div>
            <div className="flex-1 w-full">
              <RangeDatePicker
                startDate={formData.dateRange?.start}
                endDate={formData.dateRange?.end}
                onChange={handleDateRangeChange}
                placeholderText={t("workorder:filters:date:placeholder")}
              />
            </div>
          </div>
        </div>
        <div className="field">
          <label className="block text-sm font-medium text-gray-700 mb-1">{t("common:form:fields:disabled:label")}</label>
          <Switch checked={formData.disabled}
            onChange={(b) => handleChange(b, "disabled")} />
        </div>

        <div className="field">
          <label className="block text-sm font-medium text-gray-700 mb-1">{t("workorder:filters:missingPurchaseOrder:label")}</label>
          <div className="flex items-center gap-2">
            <Switch
              checked={formData.missingPurchaseOrder || false}
              onChange={(checked) => handleChange(checked, "missingPurchaseOrder")}
            />
            <span className="text-sm text-gray-600">{t("workorder:filters:missingPurchaseOrder:description")}</span>
          </div>
        </div>
      </div>
    </form>
  );
};

export default WorkordersFilterForm; 